import type { CSSProperties, ReactNode } from "react";
import { openConsentSettings } from "../consent.ts";
import { formatChips } from "../format.ts";
import { errorText, t } from "../i18n.ts";
import { linkHandler } from "../router.ts";

/** Sechseck mit Punkt – Logo-Zeichen und Chip-Symbol aus dem Entwurf 1b. Ohne Währungsbezug. */
export function ChipIcon({ className = "chip-icon" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1.5 21.1 6.75v10.5L12 22.5 2.9 17.25V6.75Z" fill="#e0156a" />
      <circle cx="12" cy="12" r="4.2" fill="#ffd23f" />
    </svg>
  );
}

export function Chips({ amount, label = false }: { amount: number; label?: boolean }) {
  return (
    <span className="chip-amount num" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <ChipIcon className="chip-icon" />
      {formatChips(amount)}
      {label && (
        <span className="muted" style={{ fontFamily: "var(--font-ui)", fontWeight: 800, fontSize: "0.8em" }}>
          {t.chips}
        </span>
      )}
    </span>
  );
}

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <a href="/" onClick={linkHandler("/")} className="logo" aria-label={t.appName}>
      <ChipIcon className="logo-chip" />
      {!compact && <span>{t.appName}</span>}
    </a>
  );
}

// Initialen-Avatare bekommen je Spieler eine Farbe aus der Palette des Entwurfs
const INITIAL_COLORS: [string, string][] = [
  ["#e0156a", "#fff"],
  ["#2de2e6", "#1b0b38"],
  ["#ffd23f", "#1b0b38"],
  ["#8a63d6", "#fff"],
];

// Symbol-Avatare (4b): Hintergrund, Symbolfarbe, optionaler Rand
const SYMBOL_STYLES: Record<string, { bg: string; ink: string; border?: string }> = {
  circle: { bg: "#2de2e6", ink: "#1b0b38" },
  triangle: { bg: "#ffd23f", ink: "#1b0b38" },
  diamond: { bg: "#8a63d6", ink: "#fff" },
  hexagon: { bg: "#d9c7ff", ink: "#1b0b38" },
  plus: { bg: "#fff", ink: "#e0156a" },
  ring: { bg: "#ff8fb9", ink: "#1b0b38" },
  dome: { bg: "#1b0b38", ink: "#ffd23f", border: "2px solid #8a63d6" },
};

function hashIndex(id: string, length: number): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return Math.abs(hash) % length;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const letters = parts.length > 1 ? [parts[0], parts.at(-1)] : [name.trim()];
  return (
    letters
      .map((p) => [...(p ?? "")][0] ?? "")
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"
  );
}

function Symbol({ kind }: { kind: string }) {
  const common = { className: "symbol", viewBox: "0 0 40 40", "aria-hidden": true } as const;
  switch (kind) {
    case "circle":
      return (
        <svg {...common}>
          <circle cx="20" cy="20" r="16" fill="currentColor" />
        </svg>
      );
    case "triangle":
      return (
        <svg {...common}>
          <path d="M20 4 37 35H3Z" fill="currentColor" />
        </svg>
      );
    case "diamond":
      return (
        <svg {...common}>
          <path d="M20 2 38 20 20 38 2 20Z" fill="currentColor" />
        </svg>
      );
    case "hexagon":
      return (
        <svg {...common}>
          <path d="M20 2.5 35.2 11.25v17.5L20 37.5 4.8 28.75v-17.5Z" fill="currentColor" />
        </svg>
      );
    case "plus":
      return (
        <svg {...common}>
          <path d="M15 3h10v12h12v10H25v12H15V25H3V15h12Z" fill="currentColor" />
        </svg>
      );
    case "ring":
      return (
        <svg {...common}>
          <circle cx="20" cy="20" r="13" fill="none" stroke="currentColor" strokeWidth="7" />
        </svg>
      );
    case "dome":
      return (
        <svg {...common}>
          <path d="M3 30a17 17 0 0 1 34 0Z" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
}

interface AvatarProps {
  id: string;
  name: string;
  avatar?: string;
  /** 0–1: Zeitlimit als Ring um die Kachel. */
  progress?: number | null;
  size?: number;
  out?: boolean;
}

/** Avatar-Kachel: Initialen oder Symbol, optional mit Timer-Ring. */
export function Avatar({ id, name, avatar = "initials", progress, size, out = false }: AvatarProps) {
  const symbol = SYMBOL_STYLES[avatar];
  const [bg, ink] = symbol ? [symbol.bg, symbol.ink] : (INITIAL_COLORS[hashIndex(id, INITIAL_COLORS.length)] ?? ["#e0156a", "#fff"]);
  const style = {
    "--avatar-bg": bg,
    "--avatar-ink": ink,
    ...(symbol?.border ? { "--avatar-border": symbol.border } : {}),
    ...(size ? { "--size": `${size}px` } : {}),
  } as CSSProperties;
  return (
    <span className={`avatar${out ? " out" : ""}`} style={style} aria-hidden="true">
      {symbol ? <Symbol kind={avatar} /> : initials(name)}
      {progress !== undefined && progress !== null && (
        <svg className="avatar-ring" viewBox="0 0 100 100" preserveAspectRatio="none">
          <rect className="track" x="2" y="2" width="96" height="96" rx="26" pathLength={100} />
          <rect
            className="progress"
            x="2"
            y="2"
            width="96"
            height="96"
            rx="26"
            pathLength={100}
            strokeDasharray={100}
            strokeDashoffset={100 * (1 - Math.max(0, Math.min(1, progress)))}
          />
        </svg>
      )}
    </span>
  );
}

export function Toast({ code }: { code: string | null }) {
  if (!code) return null;
  return (
    <div className="toast" role="alert">
      {errorText(code)}
    </div>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <span>{t.footerFacts}</span>
      <a href="/impressum" onClick={linkHandler("/impressum")}>
        {t.legal.imprint}
      </a>
      <a href="/nutzungsbedingungen" onClick={linkHandler("/nutzungsbedingungen")}>
        {t.legal.terms}
      </a>
      <a href="/datenschutz" onClick={linkHandler("/datenschutz")}>
        {t.legal.privacy}
      </a>
      <button type="button" className="link-button" onClick={openConsentSettings}>
        {t.consent.settings}
      </button>
    </footer>
  );
}

export function Page({ children, narrow = false }: { children: ReactNode; narrow?: boolean }) {
  return <main className={`page${narrow ? " page-narrow" : ""}`}>{children}</main>;
}

/** Kopfzeile der Inhaltsseiten: Logo links, „So funktioniert's“ rechts. */
export function SiteHeader({ right }: { right?: ReactNode }) {
  return (
    <header className="topbar">
      <Logo />
      {right ?? (
        <a className="btn btn-outline btn-pill btn-small" href="/regeln" onClick={linkHandler("/regeln")}>
          {t.howItWorks} →
        </a>
      )}
    </header>
  );
}
